/**
 * wsSizeBudget — pre-emptive client-side size budget for Y.Doc state.
 *
 * Why this exists
 * ───────────────
 * The server enforces a per-room size limit ("payload overflow"): if the
 * cumulative encoded state of a doc exceeds the budget, the connection is
 * killed with code 1008 and the per-room circuit breaker opens. This means
 * one bloated client can lock out an entire room.
 *
 * The chandler-pattern bug had a 64 MiB local Y.Doc. Every time the WS
 * connected, the client tried to upload 64 MiB; the server killed it; the
 * client reconnected and tried again. Forever.
 *
 * `wsSizeBudget` enforces the SAME budget on the client side, BEFORE we
 * try to upload. The first time the local doc grows past the per-prefix
 * threshold, we trigger the hard-stop locally — no server round-trip
 * required, no circuit-breaker disturbance for other clients.
 *
 * How it works
 * ────────────
 *   - Subscribes to the doc's `updateV2` event so we see every increment.
 *   - Tracks a running `bytesAccum` counter (the per-doc update log already
 *     does this; we use it directly when present, fall back to our own
 *     counter when the doc has no log attached).
 *   - When the counter crosses `softLimit`: emits a warning + Sentry crumb.
 *   - When the counter crosses `hardLimit`: triggers `manuallyHardStop` on
 *     the provider. The connection tears down; reconnect is blocked; the
 *     client never gets a chance to upload the oversized state.
 *
 * Per-prefix budgets
 * ──────────────────
 * Different room types have different acceptable sizes. A code editor
 * (`code:`) might legitimately have a 5 MB Y.Doc; a notification room
 * (`notification:`) should never exceed 100 KB. The defaults below mirror
 * the server-side `MAX_PAYLOAD_BYTES_BY_PREFIX` map. Override per-room via
 * the options object.
 *
 * Reference: WS robustness plan, Phase 2A.
 */

import * as SentryDefault from '@sentry/react';
import * as Y from 'yjs';
import type { WebsocketProvider } from 'y-websocket';
import { createFileLogger } from '../../utils/logger';
import { getDocUpdateLogRegistry } from './docUpdateLogRegistry';
import type { DocUpdateLog } from './docUpdateLog';
import { manuallyHardStop } from './wsHardStop';

const log = createFileLogger('WS_SIZE_BUDGET', { enabled: true });

// Per-prefix HARD limits, in bytes. Connection is hard-stopped above this.
// Mirror the server-side budgets (websocket-server enforces the same on its
// side; we just want to catch the problem one round-trip earlier).
const DEFAULT_HARD_LIMITS_BY_PREFIX: Record<string, number> = {
	'org:': 16 * 1024 * 1024,            // 16 MiB
	'doc:': 16 * 1024 * 1024,            // 16 MiB
	'note:': 4 * 1024 * 1024,            // 4 MiB
	'page:': 4 * 1024 * 1024,            // 4 MiB
	'space:': 4 * 1024 * 1024,           // 4 MiB
	'space-chat:': 2 * 1024 * 1024,      // 2 MiB
	'conversation:': 2 * 1024 * 1024,    // 2 MiB
	'notification:': 256 * 1024,         // 256 KiB
	'window-position:': 64 * 1024,       // 64 KiB
	'terminal:': 4 * 1024 * 1024,        // 4 MiB
	'dashboard:': 4 * 1024 * 1024,       // 4 MiB
	'broadcast:': 4 * 1024 * 1024,       // 4 MiB
};

// Soft limits trigger a warning at 75% of hard limit by default. Caller
// can override.
const DEFAULT_SOFT_RATIO = 0.75;

// If a room's prefix isn't in the map above, this is the catch-all hard
// limit. Conservative — prefer false positives over true positives that
// take down the org room.
const FALLBACK_HARD_LIMIT = 4 * 1024 * 1024;

interface SentryLike {
	addBreadcrumb(crumb: {
		category: string;
		message: string;
		level?: 'debug' | 'info' | 'warning' | 'error';
		data?: Record<string, unknown>;
	}): void;
}

export interface WsSizeBudgetOptions {
	/** Hard byte cap. If exceeded, the provider is hard-stopped. */
	hardLimit?: number;
	/** Soft byte cap. If exceeded, we warn but continue. Default: 75% of hardLimit. */
	softLimit?: number;
	/** Test seam — defaults to @sentry/react. */
	sentry?: SentryLike;
	/** Override the doc's update log; defaults to the singleton registry's entry. */
	updateLog?: DocUpdateLog;
}

export interface WsSizeBudgetHandle {
	dispose(): void;
	getCurrentBytes(): number;
}

function inferPrefix(roomname: string): string {
	const colon = roomname.indexOf(':');
	if (colon === -1) return roomname;
	return roomname.slice(0, colon + 1);
}

function defaultHardLimit(roomname: string): number {
	const prefix = inferPrefix(roomname);
	return DEFAULT_HARD_LIMITS_BY_PREFIX[prefix] ?? FALLBACK_HARD_LIMIT;
}

function getRoomName(provider: WebsocketProvider): string {
	return (provider as unknown as { roomname?: string }).roomname ?? '?';
}

function shortRoom(roomName: string): string {
	if (roomName.length <= 18) return roomName;
	return `${roomName.slice(0, 6)}…${roomName.slice(-10)}`;
}

/**
 * Attach a size budget watcher to a (provider, doc) pair.
 *
 * The watcher reads its current size from the per-doc update log when
 * available — this is essentially free, since we already track bytes
 * there for the worker pool. When no log is attached (rare; only for
 * docs that never went through any of the encode-via-worker call sites),
 * we attach our own updateV2 listener.
 */
export function attachSizeBudget(
	provider: WebsocketProvider,
	doc: Y.Doc,
	options: WsSizeBudgetOptions = {},
): WsSizeBudgetHandle {
	const sentry = options.sentry ?? (SentryDefault as SentryLike);
	const roomname = getRoomName(provider);
	const roomShort = shortRoom(roomname);
	const hardLimit = options.hardLimit ?? defaultHardLimit(roomname);
	const softLimit = options.softLimit ?? Math.floor(hardLimit * DEFAULT_SOFT_RATIO);

	if (softLimit >= hardLimit) {
		throw new RangeError('attachSizeBudget: softLimit must be < hardLimit');
	}

	// Use the shared per-doc update log — its bytes counter is maintained
	// by its own internal `updateV2` listener (for the worker-pool path).
	// We just read from it. If a log is supplied via `options.updateLog`,
	// use that; otherwise materialize one through the singleton registry.
	const sharedLog = options.updateLog ?? getDocUpdateLogRegistry().getOrCreate(doc);
	let softBreached = false;
	let hardBreached = false;
	let disposed = false;

	const getBytes = () => sharedLog.bytes();

	const checkBudget = () => {
		if (disposed || hardBreached) return;
		const total = getBytes();
		if (!softBreached && total >= softLimit) {
			softBreached = true;
			log.warn(
				`[wsSizeBudget] ${roomShort} crossed SOFT limit ` +
				`(${total}b >= ${softLimit}b, hard=${hardLimit}b)`,
			);
			try {
				sentry.addBreadcrumb({
					category: 'ws.size_budget',
					message: `soft-limit room=${roomShort} bytes=${total} soft=${softLimit} hard=${hardLimit}`,
					level: 'warning',
					data: { roomShort, bytes: total, softLimit, hardLimit },
				});
			} catch { /* noop */ }
		}
		if (total >= hardLimit) {
			hardBreached = true;
			log.error(
				`[wsSizeBudget] ${roomShort} crossed HARD limit ` +
				`(${total}b >= ${hardLimit}b) — triggering hard-stop`,
			);
			manuallyHardStop(provider, 'size_budget', {
				sentry,
				reason: `bytes=${total}>=${hardLimit}`,
			});
		}
	};

	// In case the log already exceeds the budget at attach time (e.g. a
	// huge doc loaded from IDB before we wired up), check immediately so
	// we hard-stop before the first sync attempt.
	checkBudget();

	// Subscribe to doc updates so we re-check on every change. updateV2
	// fires from any origin (local, remote, IDB), which is exactly what we
	// want — we care about the TOTAL size of state we'd need to upload,
	// regardless of how it got there. The handler is purely a trigger for
	// `checkBudget()`; bytes accounting lives in `sharedLog`.
	const onUpdate = () => {
		if (disposed || hardBreached) return;
		checkBudget();
	};
	doc.on('updateV2', onUpdate);

	return {
		dispose() {
			if (disposed) return;
			disposed = true;
			try { doc.off('updateV2', onUpdate); } catch { /* noop */ }
		},
		getCurrentBytes: getBytes,
	};
}

/** Test-only export of the default-limit table. */
export const _DEFAULT_HARD_LIMITS_BY_PREFIX = DEFAULT_HARD_LIMITS_BY_PREFIX;
